import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Check, LogOut, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  UserCard,
  getUserInitials,
  getUserAvatarUrl
} from '@bliks-io/ui';
import { useLogout } from '@/api/mutations/useLogout';
import { useMemberships } from '@/api/queries/useMemberships';
import { UserSettingsDialog } from '@/components/dialogs/user-settings/UserSettingsDialog';
import { useProfile } from '@/api/queries/useProfile';
import { useSwitchWorkspace } from '@/lib/auth/useSwitchWorkspace';
import { useAuthStore } from '@/stores/auth-store';

export function UserMenu() {
  const { data: user } = useProfile();
  const { t } = useTranslation();
  const logout = useLogout();
  const { memberships } = useMemberships();
  const { switchWorkspace, isSwitching } = useSwitchWorkspace();
  const currentTenantId = useAuthStore((state) => state.tenant?._id);
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { settings?: string };
  // Step-up re-auth returns via ?settings=security — reopen the settings
  // dialog on the security tab exactly where the 403 interrupted the user.
  const openedForStepUpReturn = search.settings === 'security';
  const [settingsOpen, setSettingsOpen] = useState(openedForStepUpReturn);

  const handleSettingsOpenChange = (open: boolean) => {
    setSettingsOpen(open);
    if (!open && openedForStepUpReturn) {
      // Strip the param so a refresh or reopen doesn't re-trigger the flow.
      void navigate({
        to: '.',
        search: (prev: Record<string, unknown>) => ({ ...prev, settings: undefined }),
        replace: true
      });
    }
  };

  // Return null if user data hasn't loaded yet
  if (!user) {
    return null;
  }

  const userInitials = getUserInitials(user.firstName, user.lastName);
  const avatarUrl = getUserAvatarUrl(user, 'thumbnail');

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative rounded-lg h-9 w-9">
            <Avatar>
              <AvatarImage src={avatarUrl} alt={`${user.firstName} ${user.lastName}`} />
              <AvatarFallback className="text-white bg-yellow-avatar">
                {userInitials}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-72" align="end" forceMount>
          <DropdownMenuLabel className="p-0 font-normal">
            <UserCard
              user={{
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                role: user.mainRole,
                avatar: user.avatar,
                departmentAbbreviation: user.department?.abbreviation
              }}
              variant="with-role"
              className="cursor-default"
            />
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setSettingsOpen(true)} className="cursor-pointer">
            <Settings className="w-4 h-4 mr-2" />
            <span>{t('common.Settings')}</span>
          </DropdownMenuItem>
          {memberships.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t('common.Workspaces')}
              </DropdownMenuLabel>
              {memberships.map((membership) => {
                const isCurrent = membership.tenantId === currentTenantId;
                return (
                  <DropdownMenuItem
                    key={membership.tenantId}
                    disabled={isCurrent || isSwitching}
                    onClick={() => switchWorkspace(membership.tenantId)}
                    className="cursor-pointer"
                  >
                    <span className="flex-1 truncate">{membership.name}</span>
                    {isCurrent && <Check className="w-4 h-4 ml-2" />}
                    {membership.status === 'pending' && (
                      <Badge variant="secondary" className="ml-2">
                        {t('selectWorkspacePending')}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                );
              })}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => logout.mutate()} className="cursor-pointer">
            <LogOut className="w-4 h-4 mr-2" />
            <span>{t('common.Logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <UserSettingsDialog
        open={settingsOpen}
        onOpenChange={handleSettingsOpenChange}
        initialTab={openedForStepUpReturn ? 'security' : 'profile'}
      />
    </>
  );
}
